import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatPlanLabel, mapPaddleStatusLabel } from "@/lib/featureAccess";
import { PRICING } from "@/lib/billingClient";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SURVEY_OPTIONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_useful", label: "Not useful for my work" },
  { value: "technical_issues", label: "Technical issues" },
  { value: "just_testing", label: "Just testing / temporary" },
  { value: "other", label: "Other (please specify)" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

export const SubscriptionManager = ({ ent }) => {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // post-cancel confirmation
  const [surveyReason, setSurveyReason] = useState("");
  const [surveyDetail, setSurveyDetail] = useState("");

  const load = () => {
    fetch(`${API_BASE}/api/billing/subscription`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setSub(j))
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div className="text-[12px] text-[var(--rs-text-3)]" data-testid="subscription-loading">Loading subscription…</div>;
  if (!sub || !sub.hasSubscription) return null;

  const cancelScheduled = sub.scheduledChange && sub.scheduledChange.action === "cancel";
  const endsAt = cancelScheduled ? sub.scheduledChange.effectiveAt : null;
  const isTrial = Boolean(sub.isTrial || sub.status === "trialing");
  const trialEndsAt = sub.trialEndsAt || (isTrial ? sub.nextBilledAt : null);
  // Display-only countdown computed from Paddle's own timestamps — Paddle
  // remains the billing authority (no homemade trial timer).
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000))
    : null;
  const cadKey = sub.cadence === "annual" ? "annual" : "monthly";
  const amountAfterTrial = sub.founding500
    ? PRICING.founding[cadKey].label
    : (sub.recurringPrice ? `${sub.recurringPrice}/${cadKey === "annual" ? "year" : "month"}` : PRICING.pro[cadKey].label);
  const statusLabel = mapPaddleStatusLabel(sub.status, sub.scheduledChange) || sub.status || "—";

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/subscription/portal`, {
        method: "POST", credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) throw new Error(body.detail || "Could not open the billing portal.");
      window.open(body.url, "_blank", "noopener");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/subscription/cancel`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          surveyReason: surveyReason || null,
          surveyDetail: surveyReason === "other" ? surveyDetail : null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Cancellation failed. Please try again.");
      setConfirmOpen(false);
      setResult(body);
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const doResume = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/subscription/resume`, {
        method: "POST", credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Could not resume. Please try again.");
      toast.success("Your subscription will continue — auto-renewal is back on.");
      setResult(null);
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  // ---- Post-cancellation confirmation view -------------------------------
  if (result) {
    const immediate = result.mode === "immediate_refund";
    const trialCancel = result.mode === "trial_cancel";
    return (
      <div className="mt-3 rounded-md border p-4" data-testid="cancel-confirmation"
        style={{ borderColor: "rgba(34,197,94,0.45)", background: "rgba(34,197,94,0.06)" }}>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4" style={{ color: "#22C55E" }} />
          <span className="font-semibold text-[13px]">Your subscription has been canceled.</span>
        </div>
        {trialCancel ? (
          <p className="text-[12.5px] text-[var(--rs-text-2)]" data-testid="cancel-confirmation-trial">
            Your free trial has been cancelled — you will <b>not</b> be charged.<br />
            You keep Pro access until {fmtDate(result.endsAt)}, then your account returns to the Free plan.
          </p>
        ) : immediate ? (
          result.refundStatus === "failed" || result.refundStatus === "not_requested" ? (
            <p className="text-[12.5px] text-[var(--rs-text-2)]" data-testid="cancel-confirmation-refund-manual">
              Your refund could not be processed automatically and has been flagged for manual
              review — you'll receive it within a few business days. Contact support if you have questions.<br />
              You no longer have access to Pro features.
            </p>
          ) : (
            <p className="text-[12.5px] text-[var(--rs-text-2)]" data-testid="cancel-confirmation-refund">
              A full refund{result.refundAmount ? ` of ${result.refundAmount}` : ""} is being processed.<br />
              You no longer have access to Pro features.
            </p>
          )
        ) : (
          <p className="text-[12.5px] text-[var(--rs-text-2)]" data-testid="cancel-confirmation-period">
            You'll have access until {fmtDate(result.endsAt)}. Your card will not be charged again.<br />
            Your subscription will not auto-renew.
          </p>
        )}
        <button data-testid="cancel-return-dashboard" onClick={() => navigate("/home")}
          className="mt-3 h-9 px-4 rounded-md font-semibold text-[12px]"
          style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  // ---- Subscription details ----------------------------------------------
  return (
    <div className="mt-3" data-testid="subscription-manager">
      {cancelScheduled && (
        <div className="mb-3 rounded-md border px-3 py-2 text-[12.5px]" data-testid="plan-ends-banner"
          style={{ borderColor: "rgba(255,179,0,0.5)", background: "rgba(255,179,0,0.08)", color: "var(--rs-amber)" }}>
          Scheduled for Cancellation — access available until {fmtDate(endsAt)}
        </div>
      )}
      {isTrial && !cancelScheduled && (
        <div className="mb-3 rounded-md border px-3 py-2 text-[12.5px]" data-testid="trial-banner"
          style={{ borderColor: "rgba(34,197,94,0.5)", background: "rgba(34,197,94,0.07)", color: "#86EFAC" }}>
          Free trial — {trialDaysLeft != null ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} remaining` : "active"}.
          Ends {fmtDate(trialEndsAt)}. Cancel before then to avoid the first charge.
        </div>
      )}
      <div className="text-[12.5px] text-[var(--rs-text-2)] space-y-1">
        <div><span className="text-[var(--rs-text-3)]">Plan:</span>{" "}
          <span data-testid="sub-plan-label">
            {formatPlanLabel(ent)}{sub.founding500 ? " · Founding 500" : ""}
          </span>
        </div>
        <div><span className="text-[var(--rs-text-3)]">Status:</span>{" "}
          <span data-testid="sub-status">
            {statusLabel}
            {cancelScheduled
              ? ` (ends ${fmtDate(endsAt)} — no further charges)`
              : sub.status === "active"
                ? ` (Auto-renews on ${fmtDate(sub.nextBilledAt)})`
                : ""}
          </span>
        </div>
        {isTrial && (
          <>
            <div><span className="text-[var(--rs-text-3)]">Trial Ends:</span>{" "}
              <span data-testid="sub-trial-end">{fmtDate(trialEndsAt)}{trialDaysLeft != null ? ` (${trialDaysLeft} days left)` : ""}</span>
            </div>
            <div><span className="text-[var(--rs-text-3)]">Amount After Trial:</span>{" "}
              <span data-testid="sub-amount-after-trial">{amountAfterTrial}{cancelScheduled ? " (will not be charged)" : ""}</span>
            </div>
          </>
        )}
        {!isTrial && (
          <div><span className="text-[var(--rs-text-3)]">Recurring Price:</span>{" "}
            <span data-testid="sub-recurring-price">{amountAfterTrial}</span>
          </div>
        )}
        <div><span className="text-[var(--rs-text-3)]">Next Billing Date:</span>{" "}
          <span data-testid="sub-next-billing">
            {fmtDate(sub.nextBilledAt || sub.currentPeriodEndsAt)}
            {cancelScheduled ? " (will not be charged)" : ""}
          </span>
        </div>
        {sub.paymentMethod && (
          <div><span className="text-[var(--rs-text-3)]">Payment Method:</span>{" "}
            <span data-testid="sub-payment-method">
              {sub.paymentMethod.type}{sub.paymentMethod.last4 ? ` ending in ${sub.paymentMethod.last4}` : ""}
            </span>
          </div>
        )}
      </div>

      {cancelScheduled ? (
        <button data-testid="resume-subscription-btn" onClick={doResume} disabled={busy}
          className="mt-3 h-9 px-4 rounded-md font-semibold text-[12px]"
          style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}>
          {busy ? "Working…" : "Resume Subscription"}
        </button>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="manage-billing-btn" onClick={openPortal} disabled={busy}
            className="h-9 px-4 rounded-md font-semibold text-[12px] border"
            style={{ borderColor: "var(--rs-border)", color: "var(--rs-text-1)" }}>
            Manage Billing
          </button>
          <button data-testid="cancel-subscription-btn" onClick={() => setConfirmOpen(true)} disabled={busy}
            className="h-9 px-4 rounded-md font-semibold text-[12px] border"
            style={{ borderColor: "rgba(239,68,68,0.5)", color: "rgb(252,165,165)", background: "rgba(239,68,68,0.08)" }}>
            Cancel Subscription
          </button>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(open) => {
        setConfirmOpen(open);
        if (!open) { setSurveyReason(""); setSurveyDetail(""); }
      }}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: "var(--rs-amber)" }} />
              Cancel Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-[13px] space-y-2">
                {isTrial ? (
                  <>
                    <p data-testid="cancel-dialog-trial-copy">
                      You're on your free trial. Cancellation will take effect at the end of the trial
                      ({fmtDate(trialEndsAt)}) and you will <b>not</b> be charged.<br />
                      You keep full Pro access until then.
                    </p>
                    <p className="text-[12px] opacity-80">Your subscription will not start after the trial ends.</p>
                  </>
                ) : sub.refundEligible ? (
                  <>
                    <p data-testid="cancel-dialog-refund-copy">
                      You are within the 3-day refund window. You'll receive a full refund
                      {sub.refundAmount ? ` of ${sub.refundAmount}` : ""}.<br />
                      Access will be revoked immediately.
                    </p>
                    <p className="text-[12px] opacity-80">Your subscription will not auto-renew after cancellation.</p>
                  </>
                ) : (
                  <>
                    <p data-testid="cancel-dialog-period-copy">
                      Your subscription will remain active until {fmtDate(sub.nextBilledAt || sub.currentPeriodEndsAt)}.
                      You will not be charged again.<br />
                      Your subscription will not auto-renew.
                    </p>
                    <p className="text-[12px] opacity-80">
                      You'll keep access until the end of your current billing period. Your
                      subscription will not auto-renew after cancellation.
                    </p>
                  </>
                )}
                <div className="pt-2 border-t border-[var(--rs-border)]" data-testid="cancel-survey">
                  <div className="font-semibold text-[12.5px] mb-1.5">Why are you canceling?</div>
                  <div className="space-y-1.5">
                    {SURVEY_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                        <input
                          type="radio"
                          name="cancel-survey-reason"
                          value={opt.value}
                          checked={surveyReason === opt.value}
                          onChange={() => setSurveyReason(opt.value)}
                          data-testid={`cancel-survey-${opt.value}`}
                          className="accent-[var(--rs-amber)]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {surveyReason === "other" && (
                    <textarea
                      data-testid="cancel-survey-detail"
                      value={surveyDetail}
                      onChange={(e) => setSurveyDetail(e.target.value)}
                      maxLength={500}
                      rows={2}
                      placeholder="Tell us more…"
                      className="mt-2 w-full rounded-md border bg-transparent px-2 py-1.5 text-[12.5px]"
                      style={{ borderColor: "var(--rs-border)" }}
                    />
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="cancel-dialog-confirm" onClick={(e) => { e.preventDefault(); doCancel(); }} disabled={busy || !surveyReason}
              style={{ background: "rgba(239,68,68,0.9)", color: "#fff" }}>
              {busy ? "Canceling…" : isTrial ? "Cancel Trial (No Charge)" : sub.refundEligible ? "Cancel Subscription (Full Refund)" : "Cancel Subscription"}
            </AlertDialogAction>
            <AlertDialogCancel data-testid="cancel-dialog-keep" disabled={busy}>Keep Subscription</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscriptionManager;
